import { useMemo } from "react";

import { DemandHqId } from "@buildresonance/resonance-lib-entities";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useMutation } from "react-query";
import { useDebouncedCallback } from "use-debounce";

import { DEMAND_API_DOMAIN_NAME } from "@lib/common/domainNames";

const PAGE_AUTOCOMPLETE_DEBOUNCE_DELAY_IN_MS = 150;

export interface UsePageAutoCompleteReturn {
    /** Hit this as the user types, will automatically debounce */
    debouncedQuery: (queryParam: string) => void;
    pageAutoCompletes: string[] | undefined;
}

const usePageAutoComplete = (
    demandHqId: DemandHqId | undefined,
): UsePageAutoCompleteReturn => {
    const { mutate, data } = useMutation<
        AxiosResponse<{ pageAutoCompletes: string[] }>,
        AxiosError,
        { query: string }
    >(({ query }) =>
        axios.get(`/demand-hqs/${demandHqId}/page-auto-completes`, {
            baseURL: `https://${DEMAND_API_DOMAIN_NAME}`,
            params: { query, first: 10 },
        }),
    );

    const debouncedQuery = useDebouncedCallback(
        (queryParam) => mutate({ query: queryParam }),
        PAGE_AUTOCOMPLETE_DEBOUNCE_DELAY_IN_MS,
    );

    const pageAutoCompletes = useMemo(
        () => data?.data?.pageAutoCompletes,
        [data],
    );

    return { debouncedQuery, pageAutoCompletes };
};

export { usePageAutoComplete };
